import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CardContent,
    Chip,
    Menu,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    InputAdornment,
    Typography
} from '@mui/material';
import { IconBuilding, IconChevronDown, IconSearch, IconBuildingSkyscraper } from '@tabler/icons';

import { useHistory } from 'react-router-dom';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';
import GlassCard from '../../../../../ui-component/extended/GlassCard';
import ImportDialog from '../../../../../ui-component/extended/ImportDialog';
import PaginationBar from '../../../../../ui-component/extended/PaginationBar';
import LoadingBox from '../../../../../ui-component/extended/LoadingBox';
import BulkCreateOrganizations from './BulkCreateOrganizations';
import useRequestGuard from '../../../../../hooks/useRequestGuard';

const ViewOrganization = () => {
    const [appId, setAppId] = useState(null);
    const [organizations, setOrganizations] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({ count: 0, numPages: 1 });
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [addMenuAnchor, setAddMenuAnchor] = useState(null);
    const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
    const [importDialogOpen, setImportDialogOpen] = useState(false);

    const history = useHistory();
    const { start, isCurrent } = useRequestGuard();

    const pegar_app_padrao = async () => {
        try {
            const response = await api.get('v1/apps/default/');
            const data = response.data;

            if (data?.app?.id) {
                setAppId(data.app.id);
            } else {
                setStatus({ type: 'error', message: 'App padrão não encontrado' });
            }
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao buscar app padrão' });
        }
    };

    useEffect(() => {
        pegar_app_padrao();
    }, []);

    useEffect(() => {
        if (!appId) return;
        listarOrganizacoes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, page, debouncedSearch]);

    const listarOrganizacoes = async () => {
        if (!appId) return;
        const requestId = start();
        setLoading(true);

        try {
            const params = new URLSearchParams();
            params.set('page', page);
            if (debouncedSearch) params.set('search', debouncedSearch);
            const response = await api.get(`v1/apps/${appId}/organizations/?${params.toString()}`);
            if (!isCurrent(requestId)) return;

            if (response.data) {
                setOrganizations(response.data.organizations);
                setPagination({ count: response.data.count || 0, numPages: response.data.num_pages || 1 });
            } else {
                setStatus({ type: 'error', message: 'Não foi possível carregar as empresas' });
            }
        } catch (error) {
            if (!isCurrent(requestId)) return;
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao carregar empresas' });
        } finally {
            if (isCurrent(requestId)) setLoading(false);
        }
    };

    const debouncedSetSearch = useMemo(() => {
        return debounce((value) => {
            setPage(1);
            setDebouncedSearch(value);
        }, 400);
    }, []);

    const handleChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        debouncedSetSearch(value);
    };

    const handleOpenOrganization = (organizationId) => {
        history.push(`/dashboard/config/organizations/edit/${organizationId}`);
    };

    return (
        <Box>
            <PageHeader
                icon={IconBuilding}
                gradient="blue"
                title="Empresas"
                subtitle="Gerencie as empresas cadastradas e suas unidades"
                actions={
                    <Stack direction="row" spacing={2}>
                        <TextField
                            size="small"
                            value={searchTerm}
                            onChange={handleChange}
                            placeholder="Buscar por nome, CNPJ..."
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IconSearch size={18} />
                                    </InputAdornment>
                                )
                            }}
                            sx={{ width: 240 }}
                        />

                        <Button
                            variant="contained"
                            endIcon={<IconChevronDown size={18} />}
                            onClick={(e) => setAddMenuAnchor(e.currentTarget)}
                        >
                            Adicionar empresa
                        </Button>
                        <Menu anchorEl={addMenuAnchor} open={Boolean(addMenuAnchor)} onClose={() => setAddMenuAnchor(null)}>
                            <MenuItem
                                onClick={() => {
                                    setAddMenuAnchor(null);
                                    history.push('/dashboard/config/organizations/create');
                                }}
                            >
                                Adicionar uma empresa
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    setAddMenuAnchor(null);
                                    setBulkDialogOpen(true);
                                }}
                            >
                                Adicionar várias empresas
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    setAddMenuAnchor(null);
                                    setImportDialogOpen(true);
                                }}
                            >
                                Importar planilha
                            </MenuItem>
                        </Menu>
                    </Stack>
                }
            />

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }}>
                    {status.message}
                </Alert>
            )}

            <GlassCard>
                <CardContent>
                    {loading ? (
                        <LoadingBox label="Carregando empresas..." />
                    ) : (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Nome Fantasia</TableCell>
                                    <TableCell>CNPJ</TableCell>
                                    <TableCell>Telefone</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Unidades</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {organizations.map((organization) => (
                                    <TableRow
                                        key={organization.id}
                                        hover
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => handleOpenOrganization(organization.id)}
                                    >
                                        <TableCell>
                                            <Typography fontWeight={600}>{organization.name}</Typography>
                                        </TableCell>
                                        <TableCell>{organization.cnpj || '—'}</TableCell>
                                        <TableCell>{organization.phone || '—'}</TableCell>
                                        <TableCell>{organization.email || '—'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={<IconBuildingSkyscraper size={14} />}
                                                label={organization.establishments_count || 0}
                                                size="small"
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {organizations.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">
                                            Nenhuma empresa cadastrada ainda
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </GlassCard>

            <PaginationBar page={page} numPages={pagination.numPages} count={pagination.count} onChange={setPage} />

            <BulkCreateOrganizations
                open={bulkDialogOpen}
                onClose={() => setBulkDialogOpen(false)}
                appId={appId}
                onImported={listarOrganizacoes}
            />

            <ImportDialog
                open={importDialogOpen}
                onClose={() => setImportDialogOpen(false)}
                title="Importar Empresas"
                entityLabel="empresas"
                systemFields={[
                    { key: 'name', label: 'Nome', required: true },
                    { key: 'cnpj', label: 'CNPJ', required: false },
                    { key: 'email', label: 'Email', required: false },
                    { key: 'phone', label: 'Telefone', required: false },
                    { key: 'address', label: 'Endereço', required: false },
                    { key: 'description', label: 'Descrição', required: false }
                ]}
                fieldsEndpoint={`v1/apps/${appId}/organization_fields/`}
                importUrl={`v1/apps/${appId}/organizations/import/`}
                payloadKey="organizations"
                onImported={listarOrganizacoes}
            />
        </Box>
    );
};

// Debounce utilitário para evitar spam na API
function debounce(func, delay) {
    let timer;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => func(...args), delay);
    };
}

export default ViewOrganization;
